import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

const DevOpsJourney = () => {
  const [journeyStarted, setJourneyStarted] = useState(false);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [resources, setResources] = useState([]);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    // Fetch topics and user progress from your API
    fetchTopics();
    fetchUserProgress();
  }, []);

  const fetchTopics = async () => {
    // API call to fetch topics
    const response = await fetch('/api/topics');
    const data = await response.json();
    setTopics(data);
  };

  const fetchUserProgress = async () => {
    // API call to fetch user progress
    const response = await fetch('/api/user-progress');
    const data = await response.json();
    setProgress(data);
  };

  const startJourney = () => {
    setJourneyStarted(true);
  };

  const selectTopic = async (topic) => {
    setSelectedTopic(topic);
    // Fetch resources for the selected topic
    const response = await fetch(`/api/resources/${topic.id}`);
    const data = await response.json();
    setResources(data);
  };

  const Planet = ({ topic, position, onClick }) => {
    const mesh = React.useRef();
    useFrame(() => (mesh.current.rotation.y += 0.01));

    return (
      <mesh position={position} ref={mesh} onClick={() => onClick(topic)}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial color={topic.color} />
      </mesh>
    );
  };

  const GalaxyView = () => (
    <Canvas>
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} />
      <Stars />
      <OrbitControls />
      {topics.map((topic, index) => (
        <Planet
          key={topic.id}
          topic={topic}
          position={[Math.cos(index) * 5, 0, Math.sin(index) * 5]}
          onClick={selectTopic}
        />
      ))}
    </Canvas>
  );

  const LevelView = () => (
    <div className="space-y-4">
      {resources.map((resource, index) => (
        <motion.div
          key={resource.id}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className={`p-4 rounded-lg ${
            progress[selectedTopic.id]?.completed.includes(resource.id)
              ? 'bg-green-600'
              : 'bg-gray-700'
          }`}
        >
          <h3 className="text-xl font-bold">{resource.title}</h3>
          <p>{resource.description}</p>
          <button className="mt-2 bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded">
            {progress[selectedTopic.id]?.completed.includes(resource.id)
              ? 'Revisit'
              : 'Start'}
          </button>
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="h-screen bg-gray-900 text-white p-8">
      {!journeyStarted ? (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={startJourney}
          className="text-2xl bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-full"
        >
          Start Your DevOps Journey
        </motion.button>
      ) : !selectedTopic ? (
        <GalaxyView />
      ) : (
        <div>
          <h2 className="text-3xl font-bold mb-4">{selectedTopic.name}</h2>
          <LevelView />
          <button
            onClick={() => setSelectedTopic(null)}
            className="mt-4 bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded"
          >
            Back to Galaxy
          </button>
        </div>
      )}
    </div>
  );
};

export default DevOpsJourney;